import * as React from 'react'
import { Activity, AlertTriangle, Cpu, Gauge, HardDrive, Network, Table2 } from 'lucide-react'
import type { SelfMetricsDto, ServiceMetricsDto, SystemSnapshotDto } from '@acs/shared'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { LineChart, Meter, Sparkline, type ChartPoint, type MeterTone } from '@/components/system/charts'
import {
  latestSelf,
  selfService,
  systemHealth,
  useSelfMetricsHistory,
  useSystemMetrics,
} from '@/hooks/use-system-metrics'
import { useLiveElapsed } from '@/hooks/use-relative-time'
import { cn } from '@/lib/cn'

/**
 * `/system` — what the box is doing.
 *
 * Two panels, and the split between them is the whole design. The top one is
 * this API process reporting on itself every two seconds; the bottom one is
 * Railway's account of every service in the project, on a 30-second grid about
 * 45 seconds behind. They are never averaged, never plotted on shared axes and
 * never presented as one number, because they are answers to different
 * questions asked at different times — see the note in @acs/shared's
 * `SystemMetricsDto`. Each panel says which it is and how old it is.
 */

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

function formatVcpu(value: number): string {
  return value >= 10 ? value.toFixed(0) : value.toFixed(2)
}

function formatGb(value: number): string {
  return value >= 10 ? value.toFixed(1) : value.toFixed(2)
}

function formatMs(value: number): string {
  return value >= 100 ? Math.round(value).toString() : value.toFixed(1)
}

function formatUptime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

/** Railway reports network as a cumulative GB counter, which is small early on. */
function formatTransfer(gb: number | undefined): string {
  if (gb === undefined) return '—'
  if (gb >= 1) return `${gb.toFixed(2)} GB`
  return `${(gb * 1024).toFixed(0)} MB`
}

/** Railway timestamps are epoch SECONDS; the charts want ms. */
function toChartPoints(series: readonly { ts: number; value: number }[]): ChartPoint[] {
  return series.map((p) => ({ x: p.ts * 1000, y: p.value }))
}

/** Severity for a usage-vs-limit meter. Matches `systemHealth`'s thresholds. */
function meterTone(value: number, limit: number | null): MeterTone {
  if (!limit || limit <= 0) return 'accent'
  const ratio = value / limit
  if (ratio >= 0.9) return 'danger'
  if (ratio >= 0.75) return 'warn'
  return 'accent'
}

/* ------------------------------------------------------------------ *
 * Stat tile
 * ------------------------------------------------------------------ */

function StatTile({
  label,
  value,
  unit,
  detail,
  points,
  icon: Icon,
}: {
  label: string
  value: string
  unit?: string
  detail?: React.ReactNode
  points?: ChartPoint[]
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>
}) {
  return (
    <div className="min-w-0 rounded-lg border border-line bg-s1 p-3.5">
      <div className="flex items-center gap-2 text-muted">
        <Icon aria-hidden="true" className="size-3.5 flex-none" />
        <span className="min-w-0 truncate text-[12.5px] font-medium">{label}</span>
      </div>

      {/*
        Proportional figures, deliberately not `tabular`. Equal-width digits are
        for columns that must align vertically; at this size they make a value
        like 1.04 look loose and gappy.
      */}
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className="font-display text-[26px] leading-none font-semibold tracking-[-0.02em] text-fg">
          {value}
        </span>
        {unit ? <span className="text-[12.5px] text-faint">{unit}</span> : null}
      </div>

      {detail ? <div className="mt-2">{detail}</div> : null}
      {points && points.length > 1 ? <Sparkline points={points} className="mt-2" /> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Live panel — this process
 * ------------------------------------------------------------------ */

function LivePanel({
  self,
  history,
  service,
}: {
  self: SelfMetricsDto | null
  history: SelfMetricsDto[]
  service: ServiceMetricsDto | null
}) {
  const cpuPoints = React.useMemo<ChartPoint[]>(
    () => history.map((s) => ({ x: s.at, y: s.cpu })),
    [history]
  )
  const memoryPoints = React.useMemo<ChartPoint[]>(
    () => history.map((s) => ({ x: s.at, y: s.rssGb })),
    [history]
  )
  const loopPoints = React.useMemo<ChartPoint[]>(
    () => history.map((s) => ({ x: s.at, y: s.eventLoopDelayMs })),
    [history]
  )

  if (!self) {
    return (
      <div className="rounded-lg border border-line bg-s1 p-6 text-[13px] text-muted">
        Waiting for the first sample from the API process…
      </div>
    )
  }

  const cpuLimit = service?.cpuLimit ?? null
  const memoryLimit = service?.memoryLimitGb ?? null

  return (
    <>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          icon={Cpu}
          label="CPU"
          value={formatVcpu(self.cpu)}
          unit="vCPU"
          points={cpuPoints}
          detail={
            <>
              <Meter
                value={self.cpu}
                limit={cpuLimit}
                tone={meterTone(self.cpu, cpuLimit)}
                label={`CPU against a ${cpuLimit ?? 'unknown'} vCPU limit`}
              />
              <p className="mt-1.5 text-[11.5px] text-faint">
                {cpuLimit ? `of ${cpuLimit} vCPU limit` : 'no limit reported'}
              </p>
            </>
          }
        />

        <StatTile
          icon={HardDrive}
          label="Memory (RSS)"
          value={formatGb(self.rssGb)}
          unit="GB"
          points={memoryPoints}
          detail={
            <>
              <Meter
                value={self.rssGb}
                limit={memoryLimit}
                tone={meterTone(self.rssGb, memoryLimit)}
                label={`Memory against a ${memoryLimit ?? 'unknown'} GB limit`}
              />
              <p className="mt-1.5 text-[11.5px] text-faint">
                {memoryLimit ? `of ${memoryLimit} GB limit` : 'no limit reported'}
              </p>
            </>
          }
        />

        {/*
          The headline number on this page, even though it is the smallest.

          Every ceiling this process has is single-threaded, so saturation shows
          up here long before it shows up in CPU or RSS — a box at 4% CPU with a
          900ms loop is a box that is completely busy.
        */}
        <StatTile
          icon={Gauge}
          label="Event loop delay"
          value={formatMs(self.eventLoopDelayMs)}
          unit="ms"
          points={loopPoints}
          detail={<p className="text-[11.5px] text-faint">worst stall in the last 2s</p>}
        />

        <StatTile
          icon={Activity}
          label="Live consoles"
          value={self.streams.toString()}
          detail={
            <p className="text-[11.5px] text-faint">
              SSE clients on this replica · up {formatUptime(self.uptimeMs)}
            </p>
          }
        />
      </div>

      {/*
        Two charts, never one with two y-axes.

        CPU runs in the low hundredths of a vCPU and the loop delay in
        milliseconds; sharing a plot would mean choosing an arbitrary alignment
        between the two scales, which invents a correlation the data does not
        contain. Side by side, each keeps its own honest axis.
      */}
      <div className="mt-4 grid gap-6 rounded-lg border border-line bg-s1 p-4 lg:grid-cols-2">
        <LineChart
          points={cpuPoints}
          title="CPU — last 5 minutes"
          formatValue={(v) => formatVcpu(v)}
          emptyLabel="Collecting samples…"
        />
        <LineChart
          points={loopPoints}
          title="Event loop delay — last 5 minutes"
          formatValue={(v) => `${formatMs(v)}ms`}
          emptyLabel="Collecting samples…"
        />
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ *
 * Railway panel — every service
 * ------------------------------------------------------------------ */

function ServiceCard({ service, isSelf }: { service: ServiceMetricsDto; isSelf: boolean }) {
  const cpu = service.cpu.at(-1)?.value ?? 0
  const memory = service.memoryGb.at(-1)?.value ?? 0
  const hasSamples = service.cpu.length > 0 || service.memoryGb.length > 0

  return (
    <div className="min-w-0 rounded-lg border border-line bg-s1 p-4">
      <div className="flex items-center gap-2">
        <h3 className="min-w-0 flex-1 truncate font-mono text-[13px] font-medium text-fg">
          {service.name}
        </h3>
        {isSelf ? <Badge tone="accent">This API</Badge> : null}
      </div>

      {!hasSamples ? (
        <p className="mt-3 text-[12.5px] text-faint">No samples in this window.</p>
      ) : (
        <>
          <div className="mt-3 space-y-3">
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] text-muted">CPU</span>
                <span className="font-mono text-[12px] text-fg tabular">
                  {formatVcpu(cpu)}
                  <span className="text-faint">
                    {service.cpuLimit ? ` / ${service.cpuLimit}` : ''} vCPU
                  </span>
                </span>
              </div>
              <div className="mt-1.5">
                <Meter
                  value={cpu}
                  limit={service.cpuLimit}
                  tone={meterTone(cpu, service.cpuLimit)}
                  label={`${service.name} CPU`}
                />
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] text-muted">Memory</span>
                <span className="font-mono text-[12px] text-fg tabular">
                  {formatGb(memory)}
                  <span className="text-faint">
                    {service.memoryLimitGb ? ` / ${service.memoryLimitGb}` : ''} GB
                  </span>
                </span>
              </div>
              <div className="mt-1.5">
                <Meter
                  value={memory}
                  limit={service.memoryLimitGb}
                  tone={meterTone(memory, service.memoryLimitGb)}
                  label={`${service.name} memory`}
                />
              </div>
            </div>
          </div>

          <Sparkline points={toChartPoints(service.cpu)} className="mt-3" />

          <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-faint">
            <Network aria-hidden="true" className="size-3 flex-none" />
            <span className="font-mono tabular">
              ↓ {formatTransfer(service.networkRxGb.at(-1)?.value)} · ↑{' '}
              {formatTransfer(service.networkTxGb.at(-1)?.value)}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Table view
 *
 * The WCAG-clean twin of everything above: no colour, no geometry, every value
 * the charts encode available as text. A chart is allowed to be the nicer way
 * to read this page, never the only way.
 * ------------------------------------------------------------------ */

function MetricsTable({ snapshot, self }: { snapshot: SystemSnapshotDto; self: SelfMetricsDto | null }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-s1">
      <table className="w-full text-[12.5px]">
        <caption className="sr-only">
          Current CPU, memory and network for every service, plus this API process
        </caption>
        <thead>
          <tr className="border-b border-line text-left text-[11px] tracking-[0.06em] text-faint uppercase">
            <th scope="col" className="px-3 py-2 font-semibold">Service</th>
            <th scope="col" className="px-3 py-2 font-semibold">CPU (vCPU)</th>
            <th scope="col" className="px-3 py-2 font-semibold">CPU limit</th>
            <th scope="col" className="px-3 py-2 font-semibold">Memory (GB)</th>
            <th scope="col" className="px-3 py-2 font-semibold">Memory limit</th>
            <th scope="col" className="px-3 py-2 font-semibold">Net ↓ / ↑</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {self ? (
            <tr className="border-b border-line">
              <th scope="row" className="px-3 py-2 text-left font-medium text-fg">
                @acs/api <span className="text-faint">(live, self-reported)</span>
              </th>
              <td className="px-3 py-2 text-fg">{formatVcpu(self.cpu)}</td>
              <td className="px-3 py-2 text-muted">—</td>
              <td className="px-3 py-2 text-fg">{formatGb(self.rssGb)}</td>
              <td className="px-3 py-2 text-muted">—</td>
              <td className="px-3 py-2 text-muted">—</td>
            </tr>
          ) : null}

          {snapshot.railway.services.map((service) => (
            <tr key={service.serviceId} className="border-b border-line last:border-0">
              <th scope="row" className="px-3 py-2 text-left font-medium text-fg">
                {service.name}
              </th>
              <td className="px-3 py-2 text-fg">
                {service.cpu.length > 0 ? formatVcpu(service.cpu.at(-1)?.value ?? 0) : '—'}
              </td>
              <td className="px-3 py-2 text-muted">{service.cpuLimit ?? '—'}</td>
              <td className="px-3 py-2 text-fg">
                {service.memoryGb.length > 0 ? formatGb(service.memoryGb.at(-1)?.value ?? 0) : '—'}
              </td>
              <td className="px-3 py-2 text-muted">{service.memoryLimitGb ?? '—'}</td>
              <td className="px-3 py-2 text-muted">
                {formatTransfer(service.networkRxGb.at(-1)?.value)} /{' '}
                {formatTransfer(service.networkTxGb.at(-1)?.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function SystemPage() {
  const query = useSystemMetrics()
  const history = useSelfMetricsHistory()
  const [showTable, setShowTable] = React.useState(false)

  const snapshot = query.data
  const self = latestSelf(history, snapshot)
  const service = selfService(snapshot)
  const railwayError = snapshot?.railway.error ?? null
  const health = systemHealth(self, service, railwayError)

  // The shared module clock, not a timer of this page's own — the console can
  // have a hundred of these mounted and pays for one tick.
  const selfAge = useLiveElapsed(self?.at ?? null)

  const toneBadge =
    health.tone === 'ok'
      ? 'live'
      : health.tone === 'warn'
        ? 'warn'
        : health.tone === 'danger'
          ? 'danger'
          : 'neutral'

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-[1400px] p-4 md:p-6">
        {/* -- header ------------------------------------------------ */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[19px] font-semibold tracking-[-0.015em] text-fg">
              System
            </h1>
            <p className="mt-0.5 text-[13px] text-muted">
              This API process, live — and every service Railway runs beside it.
            </p>
          </div>

          <Tooltip content={health.detail} side="bottom">
            <span>
              <Badge tone={toneBadge} pulse={health.tone === 'ok'}>
                {health.label}
              </Badge>
            </span>
          </Tooltip>

          <Button
            variant="ghost"
            size="sm"
            aria-pressed={showTable}
            onClick={() => {
              setShowTable((prev) => !prev)
            }}
          >
            <Table2 aria-hidden="true" />
            {showTable ? 'Hide table' : 'Table view'}
          </Button>
        </div>

        {/* -- live panel -------------------------------------------- */}
        <section className="mt-5" aria-labelledby="system-live">
          <div className="mb-2.5 flex items-baseline gap-2">
            <h2 id="system-live" className="text-[13px] font-semibold text-fg">
              This API process
            </h2>
            <span className="text-[12px] text-faint">
              measured in-process, every 2s{self ? ` · last sample ${selfAge} ago` : ''}
            </span>
          </div>

          <LivePanel self={self} history={history} service={service} />
        </section>

        {/* -- railway panel ----------------------------------------- */}
        <section className="mt-7" aria-labelledby="system-railway">
          <div className="mb-2.5 flex flex-wrap items-baseline gap-2">
            <h2 id="system-railway" className="text-[13px] font-semibold text-fg">
              Project services
            </h2>
            {/*
              The lag is stated rather than hidden. Somebody comparing this
              panel against the one above WILL find them disagreeing during a
              burst, and the honest explanation is that these numbers are a
              minute old — not that one of the two is wrong.
            */}
            <span className="text-[12px] text-faint">
              via Railway · 30s grid, 20–60s behind
            </span>
          </div>

          {snapshot && !snapshot.railway.configured ? (
            <div className="rounded-lg border border-line bg-s1 p-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 flex-none text-warn" />
                <div className="min-w-0 text-[13px]">
                  <p className="font-medium text-fg">Railway metrics are not configured.</p>
                  <p className="mt-1 text-muted">
                    Create a token at{' '}
                    <a
                      className="text-accent hover:underline"
                      href="https://railway.com/account/tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      railway.com/account/tokens
                    </a>{' '}
                    and set it as <code className="font-mono text-fg">RAILWAY_API_TOKEN</code> on the
                    API service. The project and environment ids are injected by Railway already.
                  </p>
                  <p className="mt-1 text-faint">
                    The live panel above needs none of this — it is measured in-process.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {railwayError ? (
                <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-line bg-warn-soft p-3 text-[12.5px]">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 flex-none text-warn" />
                  <p className="min-w-0 text-fg">
                    {railwayError}{' '}
                    <span className="text-muted">Showing the last values received.</span>
                  </p>
                </div>
              ) : null}

              <div
                className={cn(
                  'grid gap-3 md:grid-cols-2 xl:grid-cols-3',
                  // Hold the previous render while refetching rather than
                  // flashing a skeleton — no layout jump, no blink every 30s.
                  query.isFetching && !query.isLoading && 'opacity-60 transition-opacity'
                )}
              >
                {(snapshot?.railway.services ?? []).map((s) => (
                  <ServiceCard
                    key={s.serviceId}
                    service={s}
                    isSelf={s.serviceId === snapshot?.selfServiceId}
                  />
                ))}
              </div>

              {snapshot && snapshot.railway.services.length === 0 && !railwayError ? (
                <p className="rounded-lg border border-line bg-s1 p-4 text-[13px] text-muted">
                  Railway returned no services for this environment.
                </p>
              ) : null}
            </>
          )}
        </section>

        {/* -- table view -------------------------------------------- */}
        {showTable && snapshot ? (
          <section className="mt-7" aria-labelledby="system-table">
            <h2 id="system-table" className="mb-2.5 text-[13px] font-semibold text-fg">
              All values
            </h2>
            <MetricsTable snapshot={snapshot} self={self} />
          </section>
        ) : null}
      </div>
    </div>
  )
}
